/* Shared, read-only JSON polling. No database/API credentials belong in this file. */
const feeds = new Map();

export function validateFeed(value) {
  if (value?.version !== 1) throw new Error('JSON-Format: Das Feld „version“ muss die Zahl 1 enthalten.');
  if (!Array.isArray(value.metrics)) throw new Error('JSON-Format: „metrics“ muss eine Liste sein.');
  const generated = Date.parse(value.generated_at);
  if (!Number.isFinite(generated)) throw new Error('JSON-Format: „generated_at“ fehlt oder enthält kein gültiges Datum.');
  const metrics = new Map();
  for (const metric of value.metrics) {
    if (!metric || typeof metric.key !== 'string' || !Array.isArray(metric.items)) continue;
    const items = metric.items.filter(item => item && typeof item.text === 'string' && item.text.trim() && item.text.length <= 500)
      .slice(0, 20).map(item => ({ text: item.text, image_url: httpsUrl(item.image_url), product_url: httpsUrl(item.product_url), product_title: typeof item.product_title === 'string' ? item.product_title : null }));
    if (items.length) metrics.set(metric.key, items);
  }
  return { generated, metrics };
}

function httpsUrl(value) {
  if (typeof value !== 'string') return null;
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : null; }
  catch { return null; }
}

export function shuffle(items, previous, random = Math.random) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  if (result.length > 1 && result[0] === previous) [result[0], result[1]] = [result[1], result[0]];
  return result;
}

function subscribe(url, listener) {
  let feed = feeds.get(url);
  if (!feed) {
    feed = { listeners: new Set(), data: null, busy: false };
    feed.poll = async () => {
      if (document.hidden || feed.busy || !feed.listeners.size) return;
      feed.busy = true;
      feed.controller = new AbortController();
      const timeout = setTimeout(() => feed.controller.abort(), 8000);
      try {
        const response = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: feed.controller.signal });
        if (!response.ok) throw new Error(`Der Server antwortet mit HTTP ${response.status}.`);
        let payload;
        try { payload = await response.json(); }
        catch { throw new Error('Die Adresse liefert kein gültiges JSON. Möglicherweise antwortet eine Login-Seite oder PHP-Fehlermeldung.'); }
        feed.data = validateFeed(payload);
        feed.listeners.forEach(fn => fn(feed.data, null));
      } catch (error) {
        feed.listeners.forEach(fn => fn(feed.data, error));
      } finally { clearTimeout(timeout); feed.busy = false; }
    };
    feed.timer = setInterval(feed.poll, 10000);
    feed.visibility = () => { if (!document.hidden) feed.poll(); };
    document.addEventListener('visibilitychange', feed.visibility);
    feeds.set(url, feed);
  }
  feed.listeners.add(listener);
  if (feed.data) listener(feed.data, null);
  feed.poll();
  return () => {
    feed.listeners.delete(listener);
    if (!feed.listeners.size) {
      clearInterval(feed.timer);
      feed.controller?.abort();
      document.removeEventListener('visibilitychange', feed.visibility);
      feeds.delete(url);
    }
  };
}

export const SEPARATOR = '\u00a0\u00a0\u00a0·\u00a0\u00a0\u00a0';

class WatLiveData extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.selection = JSON.parse(this.querySelector('[data-selection]').textContent);
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.cycle = this.querySelector('[data-cycle]');
    this.accessible = this.querySelector('[data-accessible-text]');
    this.status = this.querySelector('[data-feed-status]');
    this.items = new Map();
    this.order = [];
    this.signature = null;
    this.layoutKey = null;
    this.hovered = false;
    this.focused = false;
    this.editorKey = null;
    this.inViewport = true;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.addEventListener('mouseenter', () => { this.hovered = true; this.syncPlayback(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.syncPlayback(); }, options);
    this.addEventListener('focusin', () => { this.focused = true; this.syncPlayback(); }, options);
    this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); this.syncPlayback(); }, options);
    document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
    this.motion.addEventListener('change', () => this.scheduleLayout(), options);
    this.addEventListener('shopify:block:select', event => {
      const block = this.selection.find(item => item.id === event.detail.blockId);
      if (!block) return;
      this.editorKey = block.key;
      this.revealSelected();
      this.syncPlayback();
    }, options);
    this.addEventListener('shopify:block:deselect', () => { this.editorKey = null; this.syncPlayback(); }, options);
    this.resize = new ResizeObserver(() => this.scheduleLayout());
    this.resize.observe(this.viewport);
    this.intersection = new IntersectionObserver(entries => {
      this.inViewport = entries[0].isIntersecting;
      this.syncPlayback();
    });
    this.intersection.observe(this);
    document.fonts?.ready.then(() => { if (this.isConnected) { this.layoutKey = null; this.scheduleLayout(); } });
    this.render();
    const url = httpsUrl(this.dataset.url);
    if (url) this.unsubscribe = subscribe(url, (data, error) => this.receive(data, error));
    else this.setStatus('Bitte eine öffentlich erreichbare HTTPS-JSON-Adresse eintragen.');

  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.events?.abort();
    cancelAnimationFrame(this.layoutFrame);
    this.resize?.disconnect();
    this.intersection?.disconnect();
    this.animation?.cancel();
    this.animation = null;
    this.started = false;
  }

  setStatus(message) { if (this.status) this.status.textContent = message; }

  receive(data, error) {
    this.items = data?.metrics || new Map();
    this.generated = data?.generated;
    const missing = this.selection.filter(item => !this.items.has(item.key)).map(item => item.key);
    if (error) {
      const reason = error.name === 'AbortError' ? 'Der Abruf dauert länger als acht Sekunden.' :
        error instanceof TypeError ? 'Der Browser kann die Datei nicht abrufen. Netzwerk, HTTPS und CORS-Freigabe prüfen. Bei einer fremden Domain ggf. wat-live-feed.php statt der JSON-Datei verwenden.' : error.message;
      this.dataset.feedState = 'error';
      this.setStatus(`${reason} ${data ? 'Letzter gültiger Stand wird angezeigt.' : 'Ersatztext wird angezeigt.'}`);
      if (this.lastError !== reason) console.warn('[watapparel Live-Laufband]', reason);
      this.lastError = reason;
    } else if (!this.selection.length) {
      this.dataset.feedState = 'no-selection';
      this.setStatus('JSON geladen. Bitte mindestens einen Kennzahl-Block hinzufügen und dort die gewünschten Daten auswählen.');
    } else {
      this.lastError = null;
      this.dataset.feedState = missing.length === this.selection.length ? 'no-matching-data' : 'ready';
      this.setStatus(`JSON geladen. Stand: ${new Date(data.generated).toLocaleTimeString('de-DE')}.${missing.length ? ` Ohne Daten: ${[...new Set(missing)].join(', ')}. Bitte Auswahl und JSON-Schlüssel vergleichen.` : ''}`);
    }
    this.render();
  }

  render() {
    const keys = [...new Set(this.selection.map(item => item.key))].filter(key => this.items.has(key));
    if (this.dataset.mode === 'random') {
      // Keep the shuffled order across live polls so the running band does not jump around.
      this.order = this.order.filter(key => keys.includes(key));
      this.order.push(...shuffle(keys.filter(key => !this.order.includes(key))));
    } else this.order = keys;
    this.entries = this.order.flatMap(key => this.items.get(key).map(item => ({ key, ...item })));
    if (!this.entries.length) this.entries = [{ key: '', text: this.dataset.fallback || '' }];
    const signature = JSON.stringify(this.entries);
    if (signature === this.signature) return;
    this.signature = signature;
    this.accessible.textContent = this.entries.map(item => item.text).join(' · ');
    this.scheduleLayout();
  }

  appendEntries(group, duplicate = false) {
    for (const item of this.entries) {
      if (!item.text) continue;
      const text = document.createElement('span');
      text.className = 'wat-live-item';
      text.dataset.metric = item.key;
      const title = item.product_title;
      const index = title && item.product_url ? item.text.indexOf(title) : -1;
      if (index >= 0) {
        const link = document.createElement('a');
        link.className = 'wat-live-product-link';
        link.href = item.product_url;
        link.textContent = title;
        if (duplicate) link.tabIndex = -1;
        text.append(document.createTextNode(item.text.slice(0, index)), link,
          document.createTextNode(item.text.slice(index + title.length)));
      } else text.textContent = item.text;
      if (duplicate) text.setAttribute('aria-hidden', 'true');
      const separator = document.createElement('span');
      separator.className = 'wat-live-separator';
      separator.textContent = SEPARATOR;
      group.append(text, separator);
    }
  }

  scheduleLayout() {
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = requestAnimationFrame(() => this.layout());
  }

  layout() {
    if (!this.isConnected || !this.entries) return;
    const viewportWidth = this.viewport.getBoundingClientRect().width;
    if (!viewportWidth) return;
    const style = getComputedStyle(this.viewport);
    const layoutKey = JSON.stringify([this.signature, viewportWidth, style.font, style.letterSpacing, this.motion.matches, this.dataset.speed]);
    if (layoutKey === this.layoutKey) return;
    this.layoutKey = layoutKey;
    const oldDuration = this.animation?.effect.getTiming().duration;
    const progress = oldDuration ? ((Number(this.animation.currentTime) || 0) % oldDuration) / oldDuration : 0;
    this.animation?.cancel();
    this.animation = null;
    this.track.querySelectorAll('[data-clone]').forEach(node => node.remove());
    this.cycle.replaceChildren();
    this.appendEntries(this.cycle);
    if (this.motion.matches) {
      return;
    }
    const singleWidth = this.cycle.getBoundingClientRect().width;
    if (singleWidth < 1) return;
    // Each half must cover the viewport, including when only one short metric is selected.
    const copies = Math.max(1, Math.ceil(viewportWidth / singleWidth));
    for (let i = 1; i < copies; i++) this.appendEntries(this.cycle, true);
    const width = this.cycle.getBoundingClientRect().width;
    const clone = this.cycle.cloneNode(true);
    clone.removeAttribute('data-cycle');
    clone.dataset.clone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a').forEach(link => { link.tabIndex = -1; });
    this.track.append(clone);
    const speed = Math.min(150, Math.max(10, Number(this.dataset.speed) || 40));
    const duration = width / speed * 1000;
    // Identical halves make the right-to-left 0 -> -width wrap seamless.
    this.animation = this.track.animate([
      { transform: 'translateX(0px)' }, { transform: `translateX(${-width}px)` }
    ], { duration, iterations: Infinity, easing: 'linear' });
    this.animation.currentTime = progress * duration;
    this.revealSelected();
    this.syncPlayback();
  }

  revealSelected() {
    if (!this.editorKey || !this.animation) return;
    const target = [...this.cycle.querySelectorAll('[data-metric]')].find(node => node.dataset.metric === this.editorKey);
    if (!target) return;
    const width = this.cycle.getBoundingClientRect().width;
    const offset = target.getBoundingClientRect().left - this.cycle.getBoundingClientRect().left;
    this.animation.currentTime = (offset % width) / width * this.animation.effect.getTiming().duration;
  }

  syncPlayback() {
    const shouldPause = this.focused || this.editorKey || document.hidden || !this.inViewport ||
      (this.dataset.pauseHover === 'true' && this.hovered) || this.motion.matches;
    if (this.animation) shouldPause ? this.animation.pause() : this.animation.play();
    this.track.dataset.paused = String(Boolean(shouldPause));
  }
}
if (!customElements.get('wat-live-data')) customElements.define('wat-live-data', WatLiveData);
